import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Play, Pause, Volume2, VolumeX, X } from 'lucide-react'
import { useBodyScrollLock } from '../hooks/useBodyScrollLock'
import { fmtTime } from '../lib/format'

interface VideoData {
  url: string           // TikTok page URL
  preview_url: string   // MP4 stream URL — played on hover / in modal
  thumbnail_url: string // JPEG poster
}

/**
 * Build a Supabase image-transform URL from a public storage object URL.
 *
 * Used as an <img> onError fallback. Some mirrored TikTok thumbnails were
 * stored as HEIC under a `.jpg` name — the mirror pipeline
 * (mirror-video-to-bucket) trusted TikTok's content-type and never
 * transcoded. Browsers can't decode HEIC in an <img>, so the direct object
 * URL renders blank. The render endpoint runs imgproxy server-side, which
 * DOES decode HEIC and re-encodes to JPEG. Returns null when `url` isn't a
 * Supabase public object URL (nothing to rewrite).
 */
export function toSupabaseRenderUrl(url: string | null | undefined): string | null {
  if (!url) return null
  const marker = '/storage/v1/object/public/'
  const i = url.indexOf(marker)
  if (i === -1) return null
  const base = url.slice(0, i)
  const path = url.slice(i + marker.length)
  return `${base}/storage/v1/render/image/public/${path}?width=480&quality=80`
}

/**
 * ThumbFallback — branded "Preview unavailable" placeholder shown inside a
 * 9:16 video tile when the thumbnail is missing or fails to load. Renders the
 * static asset at public/fallback_thumbnail.png (served from the site root).
 * Positioned `absolute inset:0` — drop it where the poster <img> would have
 * gone. objectFit:'cover' fills the 9:16 tile cleanly at any size, so it reads
 * correctly in both the small top-three strip and the larger related-videos
 * hero tiles.
 */
export function ThumbFallback() {
  return (
    <img
      src="/fallback_thumbnail.png"
      alt="Preview unavailable"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        userSelect: 'none',
        pointerEvents: 'none',
      }}
    />
  )
}

export function CreatorVideoThumbs({
  videos,
  maxWidth,
}: {
  videos?: VideoData[]
  maxWidth?: number
}) {
  const slots = [0, 1, 2].map(i => videos?.[i] ?? null)

  return (
    <div style={{
      display: 'grid',
      // minmax(0, 1fr), not a bare 1fr (which is minmax(auto, 1fr)): the auto
      // floor can grow to a thumbnail's intrinsic image width and push the
      // 3-up strip wider than a narrow card on mobile. Zero-floor tracks let
      // the tiles shrink to share whatever width the card has.
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      gap: 10,
      ...(maxWidth ? { maxWidth } : null),
    }}>
      {slots.map((v, i) =>
        v ? <VideoTile key={i} video={v} /> : <VideoTilePlaceholder key={i} />
      )}
    </div>
  )
}

function VideoTile({ video }: { video: VideoData }) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [thumbError, setThumbError] = useState(false)
  // Current thumbnail src — starts at the stored URL, falls back to the
  // render endpoint once if the direct object fails to decode (HEIC case).
  const [thumbSrc, setThumbSrc] = useState(video.thumbnail_url)
  const triedRenderRef = useRef(false)
  const tiktokUrl = video.url

  const handleMouseEnter = () => {
    if (videoRef.current) {
      videoRef.current.style.opacity = '1'
      videoRef.current.play().catch(() => {/* autoplay blocked — silent */})
    }
  }

  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.style.opacity = '0'
    }
  }

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    // No preview MP4 available — fall back to TikTok page
    if (!video.preview_url) {
      window.open(tiktokUrl, '_blank', 'noopener,noreferrer')
      return
    }
    // Pause hover preview before opening modal
    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.style.opacity = '0'
    }
    setModalOpen(true)
  }

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(e as unknown as React.MouseEvent) } }}
        style={{
          position: 'relative',
          aspectRatio: '9 / 16',
          display: 'block',
          borderRadius: 14,
          overflow: 'hidden',
          cursor: 'pointer',
          background: '#0a0a0e',
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06), 0 2px 8px rgba(0,0,0,0.30)',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Poster thumbnail — always visible until video fades in. Falls back
            to the branded "Preview unavailable" tile when the thumbnail is
            missing or fails to decode (after the one HEIC render-retry). */}
        {thumbSrc && !thumbError ? (
          <img
            src={thumbSrc}
            alt=""
            referrerPolicy="no-referrer"
            onError={() => {
              // First failure: the object may be HEIC-under-.jpg — retry
              // once through the render endpoint, which transcodes to JPEG.
              const fb = toSupabaseRenderUrl(video.thumbnail_url)
              if (!triedRenderRef.current && fb && fb !== thumbSrc) {
                triedRenderRef.current = true
                setThumbSrc(fb)
                return
              }
              setThumbError(true)
            }}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <ThumbFallback />
        )}

        {/* Video — hidden until hover */}
        <video
          ref={videoRef}
          src={video.preview_url}
          muted
          loop
          playsInline
          preload="none"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0,
            transition: 'opacity 200ms ease',
          }}
        />

        {/* Play icon overlay */}
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          transition: 'opacity 200ms ease',
        }}
          className="video-play-overlay"
        >
          <div style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: 'rgba(0,0,0,0.45)',
            border: '1px solid rgba(255,255,255,0.20)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(255,255,255,0.85)',
          }}>
            <Play size={12} fill="currentColor" strokeWidth={0} />
          </div>
        </div>

        {/* Bottom legibility gradient */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.55) 100%)',
          pointerEvents: 'none',
        }} />
      </div>

      {modalOpen && createPortal(
        <ThumbVideoModal
          src={video.preview_url}
          poster={video.thumbnail_url}
          tiktokUrl={tiktokUrl}
          onClose={() => setModalOpen(false)}
        />,
        document.body,
      )}
    </>
  )
}

/* ------------------------------------------------------------------ */
/*  Inline video modal — self-contained, mirrors VideoControls.VideoModal */
/* ------------------------------------------------------------------ */

/**
 * Inline video modal — self-contained, playable popup with custom controls.
 * Private to this file's VideoTile (creator top_video_urls). Submitted-video
 * playback (Hired Creators) uses its own SubmittedVideoModal instead, since
 * its data shape (video_storage_url/thumbnail_url/tiktok_video_url) is
 * different — keeps the two call sites free to diverge independently.
 */
function ThumbVideoModal({
  src, poster, tiktokUrl, onClose,
}: {
  src: string
  poster?: string
  tiktokUrl: string
  onClose: () => void
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const draggingRef = useRef(false)
  const [playing, setPlaying] = useState(false)
  const [muted, setMuted] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [dragging, setDragging] = useState(false)

  // Flips to true when the modal unmounts. The mount-time play() promise (and
  // the <video>'s error event) can settle AFTER the user closed the modal —
  // closing mid-buffer tears the <video> out of the DOM, which rejects the
  // still-pending play() with an AbortError. Without this guard that rejection
  // hit the TikTok fallback below, so clicking ✕ before the stream loaded
  // opened tiktok.com in a new tab.
  const closedRef = useRef(false)
  // Reset on mount, not just set on unmount — StrictMode's dev remount left
  // this permanently true, so both guards below became no-ops: a genuine
  // autoplay block or load error stopped falling back to TikTok and just showed
  // a dead player. Same class of bug as AddPaymentMethodModal's cancelledRef.
  useEffect(() => {
    closedRef.current = false
    return () => { closedRef.current = true }
  }, [])

  // Autoplay on mount
  useEffect(() => {
    const vid = videoRef.current
    if (vid) vid.play().catch((err: unknown) => {
      // AbortError = the pending play() was interrupted (modal closed or a
      // pause() raced it) — not a playback failure, so no TikTok fallback.
      if (closedRef.current || (err as DOMException)?.name === 'AbortError') return
      // Autoplay blocked or source invalid — redirect to TikTok
      window.open(tiktokUrl, '_blank', 'noopener,noreferrer')
      onClose()
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // If the video source fails to load, fall back to TikTok URL
  const handleVideoError = useCallback(() => {
    if (closedRef.current) return
    window.open(tiktokUrl, '_blank', 'noopener,noreferrer')
    onClose()
  }, [tiktokUrl, onClose])

  // Escape to close. Scroll lock lives in useBodyScrollLock, which also
  // restores the scroll position the browser zeroes when body (height:100%)
  // gets overflow:hidden — closing the modal used to dump you at the top.
  useBodyScrollLock()
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  // Time tracking
  useEffect(() => {
    const vid = videoRef.current
    if (!vid) return
    const onTime = () => { if (!draggingRef.current) setCurrentTime(vid.currentTime) }
    const onMeta = () => setDuration(vid.duration || 0)
    vid.addEventListener('timeupdate', onTime)
    vid.addEventListener('loadedmetadata', onMeta)
    vid.addEventListener('durationchange', onMeta)
    if (vid.duration && isFinite(vid.duration)) setDuration(vid.duration)
    return () => {
      vid.removeEventListener('timeupdate', onTime)
      vid.removeEventListener('loadedmetadata', onMeta)
      vid.removeEventListener('durationchange', onMeta)
    }
  }, [])

  const togglePlay = useCallback(() => {
    const vid = videoRef.current
    if (!vid) return
    if (playing) vid.pause()
    else vid.play().catch(() => {})
  }, [playing])

  const seekFromClientX = (clientX: number) => {
    const track = trackRef.current
    const vid = videoRef.current
    if (!track || !vid || !duration) return
    const rect = track.getBoundingClientRect()
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    const t = pct * duration
    vid.currentTime = t
    setCurrentTime(t)
  }

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation()
    e.currentTarget.setPointerCapture(e.pointerId)
    draggingRef.current = true
    setDragging(true)
    seekFromClientX(e.clientX)
  }
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return
    seekFromClientX(e.clientX)
  }
  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return
    draggingRef.current = false
    setDragging(false)
    e.currentTarget.releasePointerCapture(e.pointerId)
  }

  const pct = duration ? (currentTime / duration) * 100 : 0

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.92)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
        animation: 'tvm-fade-in 0.18s ease',
      }}
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        aria-label="Close"
        style={{
          position: 'fixed', top: 20, right: 20,
          width: 40, height: 40, borderRadius: '50%',
          background: 'rgba(255,255,255,0.12)',
          border: '1px solid rgba(255,255,255,0.22)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', zIndex: 10000,
          transition: 'background 0.15s ease, transform 0.15s ease',
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.22)' }}
        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.12)' }}
      >
        <X size={20} color="white" />
      </button>

      {/* Video container */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: 'relative',
          aspectRatio: '9 / 16',
          height: 'min(95vh, calc(95vw * 16 / 9))',
          maxWidth: '95vw',
          background: '#000',
          borderRadius: 14,
          overflow: 'hidden',
          boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
        }}
      >
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          muted={muted}
          playsInline
          onClick={togglePlay}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onError={handleVideoError}
          style={{
            width: '100%', height: '100%',
            objectFit: 'contain',
            display: 'block',
            cursor: 'pointer',
            background: '#000',
          }}
        />

        {/* Controls bar */}
        <div
          onClick={e => e.stopPropagation()}
          style={{
            position: 'absolute', left: 0, right: 0, bottom: 0,
            display: 'flex', flexDirection: 'column', gap: 2,
            padding: '8px 10px 10px',
            background: 'linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.45) 60%, transparent 100%)',
            zIndex: 4,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 2px', lineHeight: 1 }}>
            <span style={{
              fontSize: 10, color: 'white', fontWeight: 500,
              fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', opacity: 0.92,
            }}>
              {fmtTime(currentTime)} / {fmtTime(duration)}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Play/pause */}
            <button
              type="button"
              onClick={e => { e.stopPropagation(); togglePlay() }}
              aria-label={playing ? 'Pause' : 'Play'}
              className="tvm-btn"
            >
              {playing
                ? <Pause size={14} fill="white" color="white" />
                : <Play size={14} fill="white" color="white" />}
            </button>

            {/* Scrubber */}
            <div
              ref={trackRef}
              style={{
                flex: 1, height: 18,
                display: 'flex', alignItems: 'center',
                cursor: 'pointer', position: 'relative', touchAction: 'none',
              }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            >
              <div style={{
                position: 'absolute', left: 0, right: 0,
                height: dragging ? 5 : 4, background: 'rgba(255,255,255,0.3)',
                borderRadius: 3, pointerEvents: 'none',
                transition: 'height 0.15s ease',
              }} />
              <div style={{
                position: 'absolute', left: 0,
                width: `${pct}%`,
                height: dragging ? 5 : 4, background: 'white',
                borderRadius: 3, pointerEvents: 'none',
                transition: 'height 0.15s ease',
              }} />
              <div style={{
                position: 'absolute',
                width: dragging ? 14 : 12, height: dragging ? 14 : 12,
                borderRadius: '50%', background: 'white',
                boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
                transform: 'translate(-50%, -50%)',
                top: '50%', left: `${pct}%`,
                pointerEvents: 'none',
                transition: 'width 0.15s ease, height 0.15s ease',
              }} />
            </div>

            {/* Mute */}
            <button
              type="button"
              onClick={e => { e.stopPropagation(); setMuted(m => !m) }}
              aria-label={muted ? 'Unmute' : 'Mute'}
              className="tvm-btn"
            >
              {muted ? <VolumeX size={14} color="white" /> : <Volume2 size={14} color="white" />}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes tvm-fade-in {
          from { opacity: 0 }
          to { opacity: 1 }
        }
        .tvm-btn {
          display: inline-flex; align-items: center; justify-content: center;
          width: 26px; height: 26px;
          background: rgba(255,255,255,0.14);
          border: 1px solid rgba(255,255,255,0.18);
          border-radius: 50%;
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          color: white;
          cursor: pointer; padding: 0; flex-shrink: 0;
          transition: background 0.15s ease, transform 0.15s ease;
        }
        .tvm-btn:hover { background: rgba(255,255,255,0.26); transform: scale(1.06); }
      `}</style>
    </div>
  )
}

function VideoTilePlaceholder() {
  return (
    <div style={{
      position: 'relative',
      aspectRatio: '9 / 16',
      background: '#0a0a0e',
      borderRadius: 14,
      overflow: 'hidden',
      cursor: 'pointer',
      boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06), 0 2px 8px rgba(0,0,0,0.30)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <div style={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(255,255,255,0.14)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'rgba(255,255,255,0.55)',
      }}>
        <Play size={12} fill="currentColor" strokeWidth={0} />
      </div>
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.55) 100%)',
        pointerEvents: 'none',
      }} />
    </div>
  )
}
